import { Dialog } from '../../components/ui/Dialog';

export function AuthFlowDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Dialog open={open} onClose={onClose} title="How authentication works">
      <div className="space-y-7 text-sm leading-relaxed text-ink-secondary">
        <p>
          Sigil offers two ways to authenticate the users who will own wallets in your app. Pick
          the one that fits your stack — you can change later.
        </p>

        <section>
          <h4 className="text-sm font-semibold text-ink-primary">Bring your own (Custom OIDC)</h4>
          <p className="mt-2">
            Use this if you already run an identity provider — Keycloak, Auth0, Cognito, Firebase
            Auth, anything OIDC-compliant.
          </p>
          <ol className="mt-3 list-decimal space-y-1.5 pl-5">
            <li>
              Your user signs in to your app using your provider — password, OTP, MFA, passkey,
              social login. Sigil never participates and never sees credentials.
            </li>
            <li>
              Your app receives a JWT from your provider and passes it to the Sigil iframe via the
              SDK.
            </li>
            <li>
              Sigil validates the JWT signature against your provider's JWKS and checks{' '}
              <code className="rounded bg-surface-soft px-1 font-mono text-xs">iss</code>,{' '}
              <code className="rounded bg-surface-soft px-1 font-mono text-xs">aud</code> and{' '}
              <code className="rounded bg-surface-soft px-1 font-mono text-xs">exp</code>.
            </li>
            <li>
              If valid, Sigil identifies the user from the{' '}
              <code className="rounded bg-surface-soft px-1 font-mono text-xs">sub</code> claim. The
              wallet is created (or recovered) and bound to that identity — same user, same wallet
              next time.
            </li>
          </ol>
          <p className="mt-3 text-ink-secondary">
            <strong className="text-ink-primary">You give us:</strong> Issuer URL and Audience.
            JWKS is auto-discovered.
            <br />
            <strong className="text-ink-primary">No discovery endpoint?</strong> Tick the manual
            option in the form and paste your OpenID Provider Metadata document instead — Sigil
            reads <code className="rounded bg-surface-soft px-1 font-mono text-xs">issuer</code>{' '}
            and <code className="rounded bg-surface-soft px-1 font-mono text-xs">jwks_uri</code>{' '}
            straight from the JSON.
            <br />
            <strong className="text-ink-primary">We never see:</strong> passwords, MFA codes, your
            login UI.
          </p>
        </section>

        <section className="border-t border-line-soft pt-6">
          <h4 className="text-sm font-semibold text-ink-primary">Sigil hosted (default)</h4>
          <p className="mt-2">
            Use this if you don't want to run an identity provider. Sigil hosts a built-in email-OTP
            login inside the wallet iframe and the wallet itself becomes the user's identity.
          </p>
          <ol className="mt-3 list-decimal space-y-1.5 pl-5">
            <li>Your user opens your app. The Sigil iframe shows an email-OTP form.</li>
            <li>They enter their email and receive a 6-digit code.</li>
            <li>A wallet is created on first sign-in and recovered on subsequent ones.</li>
          </ol>
          <p className="mt-3 text-ink-secondary">
            End-user identities are scoped to your company and isolated from your portal account.
            They are stored in a separate identity store from the developers who use this portal —
            the two systems never share credentials, sessions or storage.
          </p>
        </section>

        <section className="border-t border-line-soft pt-6">
          <h4 className="text-sm font-semibold text-ink-primary">In both modes</h4>
          <ul className="mt-3 list-disc space-y-1.5 pl-5">
            <li>
              The wallet's private key only exists inside the Sigil iframe in the user's browser,
              for milliseconds, during signing.
            </li>
            <li>
              Sigil's backend, your backend, and your app code never see the private key.
            </li>
            <li>
              You configure <strong>Allowed origins</strong> below so the iframe rejects any
              embedding that isn't your app.
            </li>
          </ul>
        </section>
      </div>
    </Dialog>
  );
}
